import type { Profile } from '@zetyar/domain'
import type { SupabaseClient, User } from '@supabase/supabase-js'

interface AuthIdentityWithPersonRow {
  readonly uid: string
  readonly person: {
    readonly id: string
    readonly preferred_name: string
    readonly is_super_admin: boolean
    readonly avatar_url: string | null
    readonly phone: string | null
    readonly rut: string | null
  } | null
}

/**
 * Mirrors loadProfileFromDataConnect (GetMyPerson): resolves the current Supabase Auth user to
 * its `persons` row via `auth_identities`.
 *
 * Explicitly filters by uid rather than leaving scoping entirely to RLS: for a super admin,
 * auth_identities_select_self (0003_rls_identity.sql) is `uid = current_auth_uid() OR
 * is_super_admin()`, so RLS alone lets a super admin's query see every auth_identities row, not
 * just their own. Without this filter, .maybeSingle() throws "multiple (or no) rows returned" as
 * soon as more than one person exists in the system - which is exactly what a super admin's own
 * profile load would trigger.
 */
export async function getMyProfile(client: SupabaseClient, authUser: User): Promise<Profile | null> {
  const { data, error } = await client
    .from('auth_identities')
    .select('uid, person:persons (id, preferred_name, is_super_admin, avatar_url, phone, rut)')
    .eq('uid', authUser.id)
    .maybeSingle()

  if (error) throw error
  const row = data as unknown as AuthIdentityWithPersonRow | null
  const person = row?.person
  if (!person) return null

  const now = new Date().toISOString()
  return {
    id: person.id,
    firebaseUid: authUser.id,
    email: authUser.email ?? '',
    displayName: person.preferred_name || authUser.email || null,
    avatarUrl: person.avatar_url,
    phone: person.phone,
    rut: person.rut,
    emailVerified: authUser.email_confirmed_at != null,
    isSuperAdmin: person.is_super_admin,
    status: 'active',
    lastLoginAt: null,
    createdAt: now,
    updatedAt: now,
  }
}

/** Mirrors CreatePersonForSelf + CreateAuthIdentityForSelf: first-login bootstrap for a brand
 * new Supabase Auth user with no `persons`/`auth_identities` row yet (invite acceptance flow).
 *
 * Goes through the bootstrap_person_for_self RPC rather than two plain inserts: an
 * `INSERT ... RETURNING` on `persons` also has to satisfy persons' SELECT policy for the new row,
 * which depends on auth_identities - a row that does not exist yet at this exact moment. The RPC
 * is SECURITY DEFINER, so it bypasses that chicken-and-egg RLS check for this one bootstrap op. */
export async function createPersonForSelf(
  client: SupabaseClient,
  _authUser: User,
  preferredName: string,
): Promise<string> {
  const { data, error } = await client.rpc('bootstrap_person_for_self', {
    p_preferred_name: preferredName,
  })

  if (error) throw error
  return data as string
}
